// FeatureFlagsView — admin gerencia feature flags pra pilotar POCs e
// rollouts graduais. Cada flag tem:
//   - key (ex: "student.menu.aistudy") — identificador estável
//   - audience: all (liga pra todo mundo) / allowlist (só listados) / off
//   - plataforma alvo (filtro de UI): platform / company / teacher / student / any
//   - allowlist de usuários e grupos
//
// O cliente de cada portal consulta /api/user/feature-flags e usa
// flagEnabled(key) pra mostrar/esconder itens de menu.
//
// Layout: lista de cards à esquerda + drawer de detalhe à direita ao clicar
// em uma flag. Drawer permite editar label/audience + add/remove allowlist.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Beaker,
  Bell,
  BookOpen,
  Check,
  CheckCircle2,
  CreditCard,
  Flag,
  GamepadIcon,
  Loader2,
  MessageSquare,
  Mic,
  NotebookPen,
  Plus,
  Power,
  PowerOff,
  Search,
  Settings,
  Share2,
  Sparkles,
  StickyNote,
  Trash2,
  Upload,
  UserPlus,
  Users,
  UsersRound,
  Wand2,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { Switch } from "@app/ui/components/switch";
import {
  api,
  ApiError,
  type FeatureFlagAudience,
  type FeatureFlagPlatform,
  type FeatureFlagRow,
  type FeatureFlagStatus
} from "./api";
import { openAlert, openConfirm, openPrompt } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";
import {
  KNOWN_FLAGS,
  knownFlagsNotYetCreated,
  type KnownFlag
} from "./featureFlagsCatalog";

const phase = signal<"loading" | "ready" | "error">("loading");
const flags = signal<FeatureFlagRow[]>([]);
const errMsg = signal<string | null>(null);
const selectedId = signal<string | null>(null);
const filter = signal<FeatureFlagPlatform | "all">("all");

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listFeatureFlags();
    flags.value = r.flags;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

const PLATFORM_LABELS: Record<FeatureFlagPlatform, string> = {
  any: "Qualquer",
  platform: "Admin Plataforma",
  company: "Empresa",
  teacher: "Professor",
  student: "Aluno"
};

const AUDIENCE_LABELS: Record<FeatureFlagAudience, string> = {
  all: "Todos",
  allowlist: "Allowlist",
  off: "Desligada"
};

// Catálogo de "áreas" — mapeia padrões de key pra um ícone + cor + rótulo
// humano. Admin não precisa decorar `student.menu.aistudy`; vê
// "Estudo com IA" + ícone de varinha + chip violeta. Ordem importa: o
// primeiro padrão que casa vence (specific → generic).
const AREA_CATALOG: Array<{
  match: RegExp;
  label: string;
  icon: typeof Flag;
  tint: string; // tailwind: bg+text classes pro halo do ícone
}> = [
  { match: /\bai\b|aistudy|gemini|openai|llm/i, label: "IA", icon: Wand2, tint: "bg-violet-500/15 text-violet-600 dark:text-violet-300" },
  { match: /recorder|gravador|lecture/i, label: "Gravador", icon: Mic, tint: "bg-rose-500/15 text-rose-600 dark:text-rose-300" },
  { match: /payment|cobranca|pix|assinatura|plano/i, label: "Pagamentos", icon: CreditCard, tint: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" },
  { match: /notif|notification/i, label: "Notificações", icon: Bell, tint: "bg-amber-500/15 text-amber-600 dark:text-amber-300" },
  { match: /mensagem|message|chat|peer/i, label: "Mensagens", icon: MessageSquare, tint: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
  { match: /share|compartilh/i, label: "Compartilhamento", icon: Share2, tint: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-300" },
  { match: /notes|anota|caderno/i, label: "Anotações", icon: StickyNote, tint: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-300" },
  { match: /bank|banco|prova|quest/i, label: "Bancos & provas", icon: BookOpen, tint: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300" },
  { match: /games?|jogo/i, label: "Jogos", icon: GamepadIcon, tint: "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300" },
  { match: /menu|sidebar|nav/i, label: "Menu / Navegação", icon: NotebookPen, tint: "bg-slate-500/15 text-slate-600 dark:text-slate-300" },
  { match: /admin|manager|config/i, label: "Configurações", icon: Settings, tint: "bg-zinc-500/15 text-zinc-600 dark:text-zinc-300" }
];

function getArea(key: string, label: string): { label: string; icon: typeof Flag; tint: string } {
  const hay = `${key} ${label}`;
  for (const a of AREA_CATALOG) {
    if (a.match.test(hay)) return { label: a.label, icon: a.icon, tint: a.tint };
  }
  return { label: "Geral", icon: Flag, tint: "bg-muted text-muted-foreground" };
}

const STATUS_META: Record<
  FeatureFlagStatus,
  { label: string; icon: typeof Beaker; bg: string; fg: string; ring: string }
> = {
  experimental: {
    label: "Experimental",
    icon: Beaker,
    bg: "bg-amber-500/15",
    fg: "text-amber-700 dark:text-amber-300",
    ring: "ring-amber-500/40"
  },
  beta: {
    label: "Beta",
    icon: Sparkles,
    bg: "bg-blue-500/15",
    fg: "text-blue-700 dark:text-blue-300",
    ring: "ring-blue-500/40"
  },
  stable: {
    label: "Estável",
    icon: CheckCircle2,
    bg: "bg-emerald-500/15",
    fg: "text-emerald-700 dark:text-emerald-300",
    ring: "ring-emerald-500/40"
  },
  deprecated: {
    label: "Depreciada",
    icon: PowerOff,
    bg: "bg-muted",
    fg: "text-muted-foreground",
    ring: "ring-border"
  }
};

export function FeatureFlagsView() {
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function createFromCatalog(item: KnownFlag, audience: FeatureFlagAudience) {
    try {
      const r = await api.createFeatureFlag({
        key: item.key,
        label: item.label,
        description: item.description,
        platform: item.platform,
        audience,
        status: item.defaultStatus
      });
      await load();
      selectedId.value = r.flag.id;
      setCreateOpen(false);
    } catch (e) {
      void openAlert({ title: "Erro ao criar", message: friendlyError(e) });
    }
  }

  async function createCustomFlag(data: {
    key: string;
    label: string;
    description: string;
    platform: FeatureFlagPlatform;
    audience: FeatureFlagAudience;
  }) {
    try {
      const r = await api.createFeatureFlag({
        key: data.key.trim(),
        label: data.label.trim(),
        description: data.description.trim(),
        platform: data.platform,
        audience: data.audience,
        status: "experimental"
      });
      await load();
      selectedId.value = r.flag.id;
      setCreateOpen(false);
    } catch (e) {
      void openAlert({ title: "Erro ao criar", message: friendlyError(e) });
    }
  }

  if (phase.value === "loading") {
    return (
      <div class="space-y-2">
        <Skeleton class="h-12 w-full" />
        <Skeleton class="h-64 w-full" />
      </div>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }

  const platform = filter.value;
  const filtered =
    platform === "all" ? flags.value : flags.value.filter((f) => f.platform === platform);
  const selected = flags.value.find((f) => f.id === selectedId.value);

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm">
        <Flag className="mr-1 inline size-4 text-amber-600" />
        Pilote POCs e faça rollout gradual. Uma flag em{" "}
        <strong>allowlist</strong> só liga pra usuários/grupos listados;{" "}
        <strong>todos</strong> liga pra plataforma inteira; <strong>off</strong>{" "}
        é kill switch. Os portais consultam <code>flagEnabled(key)</code> pra
        mostrar/esconder itens.
      </div>

      <header class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex flex-wrap gap-1">
          {(["all", "platform", "company", "teacher", "student", "any"] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => (filter.value = p)}
              class={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                filter.value === p
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-accent"
              }`}
            >
              {p === "all" ? "Todas" : PLATFORM_LABELS[p]}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          class="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:brightness-110"
        >
          <Plus className="size-3.5" />
          Nova flag
        </button>
      </header>

      {createOpen && (
        <CreateFlagModal
          existingKeys={flags.value.map((f) => f.key)}
          onCancel={() => setCreateOpen(false)}
          onCreateFromCatalog={createFromCatalog}
          onCreateCustom={createCustomFlag}
        />
      )}

      <div class="grid gap-4 lg:grid-cols-[1fr_minmax(0,28rem)]">
        <div class="space-y-2">
          {filtered.length === 0 ? (
            <div class="rounded-md border border-dashed border-border bg-muted/30 px-4 py-12 text-center text-sm text-muted-foreground">
              Nenhuma flag criada ainda. Clique em "Nova flag" pra começar.
            </div>
          ) : (
            filtered.map((f) => (
              <FlagRow
                key={f.id}
                flag={f}
                selected={selected?.id === f.id}
                onSelect={() => (selectedId.value = f.id)}
              />
            ))
          )}
        </div>

        {selected && (
          <FlagDetail key={selected.id} flag={selected} onClose={() => (selectedId.value = null)} />
        )}
      </div>
    </div>
  );
}

function FlagRow({
  flag,
  selected,
  onSelect
}: {
  flag: FeatureFlagRow;
  selected: boolean;
  onSelect: () => void;
}) {
  const [busyToggle, setBusyToggle] = useState(false);
  // Toggle inline: liga/desliga a flag rapidamente.
  //   - 'off' ↔ 'allowlist' (preserva allowlist quando desligar)
  //   - se a flag estiver 'all', toggle pra 'off' como kill switch
  const isOn = flag.audience !== "off";

  async function quickToggle(e: MouseEvent) {
    e.stopPropagation();
    setBusyToggle(true);
    try {
      const nextAudience: FeatureFlagAudience = isOn
        ? "off"
        : flag.userCount && flag.userCount > 0
          ? "allowlist"
          : "all";
      await api.updateFeatureFlag(flag.id, { audience: nextAudience });
      await load();
    } catch (err) {
      void openAlert({
        title: "Erro",
        message: friendlyError(err)
      });
    } finally {
      setBusyToggle(false);
    }
  }

  const area = getArea(flag.key, flag.label);
  const AreaIcon = area.icon;
  return (
    <div
      class={`flex w-full items-stretch gap-3 rounded-lg border px-4 py-3.5 text-left transition-all ${
        selected
          ? "border-primary bg-primary/5 shadow-sm"
          : "border-border bg-background hover:border-primary/40 hover:shadow-sm"
      } ${!isOn ? "opacity-60" : ""}`}
    >
      <button
        type="button"
        onClick={onSelect}
        class="flex min-w-0 flex-1 items-start gap-3 text-left"
      >
        {/* Ícone grande de área — admin vê de relance o que a flag mexe */}
        <div
          class={`flex size-12 shrink-0 items-center justify-center rounded-lg ${area.tint}`}
          title={area.label}
        >
          <AreaIcon className="size-6" />
        </div>
        <div class="min-w-0 flex-1">
          {/* Linha 1: label grande + chip de plataforma */}
          <div class="flex items-center gap-2">
            <span class="truncate text-base font-semibold leading-tight">
              {flag.label}
            </span>
            <PlatformChip platform={flag.platform} />
          </div>
          {/* Linha 2: descrição prominente (o que essa flag faz) */}
          {flag.description ? (
            <p class="mt-1 line-clamp-2 text-sm text-muted-foreground">
              {flag.description}
            </p>
          ) : (
            <p class="mt-1 text-xs italic text-muted-foreground/70">
              Sem descrição — clique pra editar e explicar o que essa flag faz.
            </p>
          )}
          {/* Linha 3: status + audiência + contadores */}
          <div class="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
            <span class="rounded-full bg-muted/50 px-2 py-0.5 text-muted-foreground">
              {area.label}
            </span>
            <StatusBadge status={flag.status} />
            <AudienceBadge audience={flag.audience} />
            {flag.audience === "allowlist" && (
              <span class="text-muted-foreground">
                <Users className="mr-1 inline size-3" />
                {flag.userCount ?? 0}
                <span class="mx-1">·</span>
                <UsersRound className="mr-1 inline size-3" />
                {flag.groupCount ?? 0}
              </span>
            )}
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={quickToggle}
        disabled={busyToggle}
        title={isOn ? "Desligar (kill switch)" : "Reativar"}
        aria-label={isOn ? "Desligar flag" : "Reativar flag"}
        class={`shrink-0 rounded-md p-1.5 transition-colors ${
          isOn
            ? "text-emerald-600 hover:bg-destructive/10 hover:text-destructive dark:text-emerald-400"
            : "text-destructive hover:bg-emerald-500/10 hover:text-emerald-600"
        } disabled:opacity-50`}
      >
        {busyToggle ? (
          <Loader2 className="size-4 animate-spin" />
        ) : isOn ? (
          <Power className="size-4" />
        ) : (
          <PowerOff className="size-4" />
        )}
      </button>
    </div>
  );
}

function AudienceBadge({ audience }: { audience: FeatureFlagAudience }) {
  const styles: Record<FeatureFlagAudience, string> = {
    all: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    allowlist: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
    off: "bg-destructive/10 text-destructive"
  };
  return (
    <span
      class={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${styles[audience]}`}
    >
      {AUDIENCE_LABELS[audience]}
    </span>
  );
}

function StatusBadge({ status }: { status: FeatureFlagStatus }) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <span
      class={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${meta.bg} ${meta.fg} ${meta.ring}`}
    >
      <Icon className="size-3" />
      {meta.label}
    </span>
  );
}

function PlatformChip({ platform }: { platform: FeatureFlagPlatform }) {
  return (
    <span class="rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
      {PLATFORM_LABELS[platform]}
    </span>
  );
}

function FlagDetail({
  flag,
  onClose
}: {
  flag: FeatureFlagRow;
  onClose: () => void;
}) {
  const [label, setLabel] = useState(flag.label);
  const [description, setDescription] = useState(flag.description);
  const [audience, setAudience] = useState<FeatureFlagAudience>(flag.audience);
  const [platform, setPlatform] = useState<FeatureFlagPlatform>(flag.platform);
  const [status, setStatus] = useState<FeatureFlagStatus>(flag.status);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<
    Array<{ userId: string; userName: string; userEmail: string }>
  >([]);
  const [groupRows, setGroupRows] = useState<
    Array<{ groupId: string; groupName: string }>
  >([]);
  const [deniedUsers, setDeniedUsers] = useState<
    Array<{ userId: string; userName: string; userEmail: string }>
  >([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<{
    added: number;
    alreadyIn: number;
    notFound: string[];
  } | null>(null);

  async function loadDetails() {
    try {
      const r = await api.getFeatureFlag(flag.id);
      setUsers(r.users);
      setGroupRows(r.groups);
      setDeniedUsers(r.usersDenied ?? []);
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    void loadDetails();
  }, [flag.id]);

  const dirty =
    label !== flag.label ||
    description !== flag.description ||
    audience !== flag.audience ||
    platform !== flag.platform ||
    status !== flag.status;

  async function save() {
    setSaving(true);
    try {
      await api.updateFeatureFlag(flag.id, {
        label,
        description,
        audience,
        platform,
        status
      });
      await load();
    } catch (e) {
      void openAlert({
        title: "Erro ao salvar",
        message: friendlyError(e)
      });
    } finally {
      setSaving(false);
    }
  }

  async function bulkAddSubmit() {
    const emails = bulkText
      .split(/[\n,;]+/g)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (emails.length === 0) {
      void openAlert({
        title: "Sem emails",
        message: "Cole pelo menos 1 email (1 por linha, ou separados por vírgula/ponto-e-vírgula)."
      });
      return;
    }
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const r = await api.bulkAddFlagUsers(flag.id, emails);
      setBulkResult({
        added: r.added,
        alreadyIn: r.alreadyIn,
        notFound: r.notFound
      });
      setBulkText("");
      await Promise.all([loadDetails(), load()]);
    } catch (e) {
      void openAlert({
        title: "Erro no bulk add",
        message: friendlyError(e)
      });
    } finally {
      setBulkBusy(false);
    }
  }

  async function addUser() {
    const email = await openPrompt({
      title: "Adicionar usuário",
      message: "Digite o email exato do usuário a habilitar a flag.",
      placeholder: "aluno@exemplo.com",
      confirmLabel: "Buscar e adicionar"
    });
    if (!email) return;
    try {
      const r = await api.listUsers();
      const needle = email.trim().toLowerCase();
      const u = r.users.find((x) => x.email.toLowerCase() === needle);
      if (!u) {
        void openAlert({
          title: "Não encontrado",
          message: `Nenhum usuário com email ${email}.`
        });
        return;
      }
      await api.addFlagUser(flag.id, u.id);
      await Promise.all([loadDetails(), load()]);
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    }
  }

  async function removeUser(userId: string) {
    const ok = await openConfirm({
      title: "Remover usuário?",
      message: "A flag deixará de estar ligada pra esse usuário (se a audience for allowlist).",
      confirmLabel: "Remover",
      danger: true
    });
    if (!ok) return;
    try {
      await api.removeFlagUser(flag.id, userId);
      await Promise.all([loadDetails(), load()]);
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    }
  }

  async function addUserDenied() {
    const email = await openPrompt({
      title: "Bloquear usuário (denylist)",
      message:
        "Digite o email do usuário pra desligar a flag SÓ pra ele. A denylist vence allowlist e audience='all'.",
      placeholder: "aluno@exemplo.com",
      confirmLabel: "Bloquear"
    });
    if (!email) return;
    try {
      const r = await api.listUsers();
      const needle = email.trim().toLowerCase();
      const u = r.users.find((x) => x.email.toLowerCase() === needle);
      if (!u) {
        void openAlert({
          title: "Não encontrado",
          message: `Nenhum usuário com email ${email}.`
        });
        return;
      }
      await api.addFlagUserDenied(flag.id, u.id);
      await Promise.all([loadDetails(), load()]);
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    }
  }

  async function removeUserDenied(userId: string) {
    const ok = await openConfirm({
      title: "Remover do denylist?",
      message: "O usuário volta a seguir as regras normais da flag.",
      confirmLabel: "Remover",
      danger: false
    });
    if (!ok) return;
    try {
      await api.removeFlagUserDenied(flag.id, userId);
      await Promise.all([loadDetails(), load()]);
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    }
  }

  async function addGroup() {
    const name = await openPrompt({
      title: "Adicionar grupo (turma)",
      message: "Digite o nome do grupo. Vamos buscar pelo nome — exato preferido, depois primeiro match.",
      placeholder: "Turma 2026A",
      confirmLabel: "Buscar e adicionar"
    });
    if (!name) return;
    try {
      const r = await api.listGroups();
      const needle = name.trim().toLowerCase();
      const g =
        r.groups.find((x) => x.name.toLowerCase() === needle) ??
        r.groups.find((x) => x.name.toLowerCase().includes(needle));
      if (!g) {
        void openAlert({ title: "Não encontrado", message: `Grupo "${name}".` });
        return;
      }
      await api.addFlagGroup(flag.id, g.id);
      await Promise.all([loadDetails(), load()]);
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    }
  }

  async function removeGroup(groupId: string) {
    try {
      await api.removeFlagGroup(flag.id, groupId);
      await Promise.all([loadDetails(), load()]);
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    }
  }

  async function destroy() {
    const ok = await openConfirm({
      title: "Excluir flag?",
      message: `A flag "${flag.label}" será removida com todas as assignments. Não dá pra desfazer.`,
      confirmLabel: "Excluir",
      danger: true
    });
    if (!ok) return;
    try {
      await api.deleteFeatureFlag(flag.id);
      onClose();
      await load();
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    }
  }

  return (
    <aside class="space-y-3 rounded-md border border-border bg-card p-5 lg:sticky lg:top-4 lg:self-start">
      <header class="flex items-start justify-between gap-2">
        <div class="flex min-w-0 items-center gap-3">
          {(() => {
            const area = getArea(flag.key, flag.label);
            const AreaIcon = area.icon;
            return (
              <div
                class={`flex size-11 shrink-0 items-center justify-center rounded-lg ${area.tint}`}
                title={area.label}
              >
                <AreaIcon className="size-5" />
              </div>
            );
          })()}
          <div class="min-w-0">
            <h2 class="truncate text-base font-bold leading-tight">{flag.label}</h2>
            <div class="mt-0.5 flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <span class="rounded-sm bg-muted/60 px-1.5 py-0.5 font-medium">
                ID técnico
              </span>
              <code class="truncate font-mono">{flag.key}</code>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          class="rounded-md p-1 text-muted-foreground hover:bg-accent"
        >
          <X className="size-4" />
        </button>
      </header>

      <div class="grid grid-cols-3 gap-2">
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Status
          </label>
          <select
            value={status}
            onChange={(e) =>
              setStatus(
                (e.target as HTMLSelectElement).value as FeatureFlagStatus
              )
            }
            title="Ciclo de vida"
            class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          >
            <option value="experimental">Experimental</option>
            <option value="beta">Beta</option>
            <option value="stable">Estável</option>
            <option value="deprecated">Depreciada</option>
          </select>
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Audiência
          </label>
          <select
            value={audience}
            onChange={(e) =>
              setAudience(
                (e.target as HTMLSelectElement).value as FeatureFlagAudience
              )
            }
            title="Audiência"
            class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          >
            <option value="all">Todos</option>
            <option value="allowlist">Allowlist</option>
            <option value="off">Desligada (kill)</option>
          </select>
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Plataforma
          </label>
          <select
            value={platform}
            onChange={(e) =>
              setPlatform(
                (e.target as HTMLSelectElement).value as FeatureFlagPlatform
              )
            }
            title="Plataforma"
            class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          >
            <option value="any">Qualquer</option>
            <option value="platform">Admin</option>
            <option value="company">Empresa</option>
            <option value="teacher">Professor</option>
            <option value="student">Aluno</option>
          </select>
        </div>
      </div>

      <div>
        <label class="mb-1 block text-xs font-medium text-muted-foreground">
          Rótulo
        </label>
        <input
          type="text"
          value={label}
          onInput={(e) => setLabel((e.target as HTMLInputElement).value)}
          placeholder="Rótulo da flag"
          title="Rótulo amigável"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        />
      </div>

      <div>
        <label class="mb-1 block text-xs font-medium text-muted-foreground">
          Descrição
        </label>
        <textarea
          value={description}
          onInput={(e) =>
            setDescription((e.target as HTMLTextAreaElement).value)
          }
          rows={2}
          placeholder="Para que serve essa flag?"
          title="Descrição"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        />
      </div>

      {audience === "allowlist" && (
        <>
          <section>
            <div class="mb-1 flex items-center justify-between">
              <h3 class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Users className="mr-1 inline size-3" />
                Usuários ({users.length})
              </h3>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setBulkOpen((v) => !v)}
                  class={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] transition-colors ${
                    bulkOpen
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background hover:bg-accent"
                  }`}
                  title="Adicionar vários por lista de emails"
                >
                  <Upload className="size-3" />
                  Bulk
                </button>
                <button
                  type="button"
                  onClick={addUser}
                  class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[10px] hover:bg-accent"
                >
                  <UserPlus className="size-3" />
                  Adicionar
                </button>
              </div>
            </div>
            {bulkOpen && (
              <div class="mb-2 space-y-2 rounded-md border border-primary/30 bg-primary/5 p-2">
                <p class="text-[10px] text-muted-foreground">
                  Cole emails separados por vírgula, ponto-e-vírgula ou um por
                  linha. Resolvemos email exato OU normalizado (Gmail-style,
                  ignora pontos).
                </p>
                <textarea
                  value={bulkText}
                  onInput={(e) =>
                    setBulkText((e.target as HTMLTextAreaElement).value)
                  }
                  rows={4}
                  placeholder={"aluno1@exemplo.com\naluno2@exemplo.com\naluno3@exemplo.com"}
                  class="w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px]"
                />
                <div class="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setBulkOpen(false);
                      setBulkText("");
                      setBulkResult(null);
                    }}
                    class="rounded-md border border-border bg-background px-2 py-1 text-[10px] hover:bg-accent"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={bulkAddSubmit}
                    disabled={bulkBusy || !bulkText.trim()}
                    class="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[10px] font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
                  >
                    {bulkBusy ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : (
                      <Check className="size-3" />
                    )}
                    Adicionar
                  </button>
                </div>
                {bulkResult && (
                  <div class="space-y-1 rounded-md border border-border bg-background/60 px-2 py-1.5 text-[11px]">
                    <div>
                      <span class="text-emerald-600 dark:text-emerald-400">
                        ✓ {bulkResult.added} adicionado{bulkResult.added === 1 ? "" : "s"}
                      </span>
                      {bulkResult.alreadyIn > 0 && (
                        <span class="ml-2 text-muted-foreground">
                          · {bulkResult.alreadyIn} já {bulkResult.alreadyIn === 1 ? "estava" : "estavam"}
                        </span>
                      )}
                    </div>
                    {bulkResult.notFound.length > 0 && (
                      <div class="text-amber-700 dark:text-amber-300">
                        ⚠ {bulkResult.notFound.length} email{bulkResult.notFound.length === 1 ? "" : "s"} não encontrado{bulkResult.notFound.length === 1 ? "" : "s"}:
                        <div class="mt-0.5 break-all font-mono text-[10px] text-muted-foreground">
                          {bulkResult.notFound.join(", ")}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {users.length === 0 ? (
              <p class="text-[11px] text-muted-foreground">
                Nenhum usuário na allowlist.
              </p>
            ) : (
              <ul class="space-y-1">
                {users.map((u) => (
                  <li
                    key={u.userId}
                    class="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-xs"
                  >
                    <div class="min-w-0 flex-1">
                      <div class="truncate font-medium">{u.userName}</div>
                      <div class="truncate text-[10px] text-muted-foreground">
                        {u.userEmail}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeUser(u.userId)}
                      aria-label="Remover"
                      title="Remover"
                      class="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="size-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <div class="mb-1 flex items-center justify-between">
              <h3 class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <UsersRound className="mr-1 inline size-3" />
                Grupos ({groupRows.length})
              </h3>
              <button
                type="button"
                onClick={addGroup}
                class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[10px] hover:bg-accent"
              >
                <Search className="size-3" />
                Adicionar
              </button>
            </div>
            {groupRows.length === 0 ? (
              <p class="text-[11px] text-muted-foreground">
                Nenhum grupo na allowlist.
              </p>
            ) : (
              <ul class="space-y-1">
                {groupRows.map((g) => (
                  <li
                    key={g.groupId}
                    class="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-xs"
                  >
                    <span class="truncate">{g.groupName}</span>
                    <button
                      type="button"
                      onClick={() => removeGroup(g.groupId)}
                      aria-label="Remover"
                      title="Remover"
                      class="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="size-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      <section class="rounded-md border border-red-500/30 bg-red-500/5 p-3">
        <div class="mb-2 flex items-center justify-between">
          <h3 class="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-red-700 dark:text-red-300">
            <PowerOff className="size-3" />
            Denylist ({deniedUsers.length})
          </h3>
          <button
            type="button"
            onClick={addUserDenied}
            class="inline-flex items-center gap-1 rounded-md border border-red-500/40 bg-background px-2 py-1 text-[10px] text-red-700 hover:bg-red-500/10 dark:text-red-300"
          >
            <UserPlus className="size-3" />
            Bloquear usuário
          </button>
        </div>
        <p class="mb-2 text-[10px] text-muted-foreground">
          A flag fica SEMPRE desligada pra estes usuários — vence allowlist e
          audience='all'. Use pra desligar uma feature pra alguém após bug
          report ou pra excluir um usuário específico de um rollout.
        </p>
        {deniedUsers.length === 0 ? (
          <p class="text-[11px] text-muted-foreground">
            Nenhum usuário bloqueado.
          </p>
        ) : (
          <ul class="space-y-1">
            {deniedUsers.map((u) => (
              <li
                key={u.userId}
                class="flex items-center justify-between gap-2 rounded-md border border-red-500/20 bg-background px-2 py-1.5 text-xs"
              >
                <div class="min-w-0 flex-1">
                  <div class="truncate font-medium">{u.userName}</div>
                  <div class="truncate text-[10px] text-muted-foreground">
                    {u.userEmail}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeUserDenied(u.userId)}
                  aria-label="Remover do denylist"
                  title="Remover do denylist"
                  class="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <Trash2 className="size-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div class="flex items-center justify-between gap-2 border-t border-border pt-3">
        <button
          type="button"
          onClick={destroy}
          class="inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="size-3" />
          Excluir
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          class="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Check className="size-4" />
          )}
          Salvar
        </button>
      </div>
    </aside>
  );
}

// ===========================================================================
// CreateFlagModal — picker humano: escolhe do catálogo de flags conhecidas
// (que o código realmente usa via flagEnabled) ou define uma custom. Tira o
// peso de "adivinhar a key" do admin. [[project-feature-flag-admin-revamp]]
// ===========================================================================

function CreateFlagModal({
  existingKeys,
  onCancel,
  onCreateFromCatalog,
  onCreateCustom
}: {
  existingKeys: string[];
  onCancel: () => void;
  onCreateFromCatalog: (item: KnownFlag, audience: FeatureFlagAudience) => Promise<void>;
  onCreateCustom: (data: {
    key: string;
    label: string;
    description: string;
    platform: FeatureFlagPlatform;
    audience: FeatureFlagAudience;
  }) => Promise<void>;
}) {
  const [tab, setTab] = useState<"catalog" | "custom">("catalog");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  // Custom form
  const [cKey, setCKey] = useState("");
  const [cLabel, setCLabel] = useState("");
  const [cDesc, setCDesc] = useState("");
  const [cPlatform, setCPlatform] = useState<FeatureFlagPlatform>("any");
  const [cAudience, setCAudience] = useState<FeatureFlagAudience>("allowlist");

  const availableFromCatalog = knownFlagsNotYetCreated(existingKeys);
  const alreadyCreatedFromCatalog = KNOWN_FLAGS.filter((f) =>
    existingKeys.includes(f.key)
  );

  const filteredCatalog = availableFromCatalog.filter((f) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      f.label.toLowerCase().includes(q) ||
      f.key.toLowerCase().includes(q) ||
      f.description.toLowerCase().includes(q)
    );
  });

  async function pickFromCatalog(item: KnownFlag, audience: FeatureFlagAudience) {
    setBusy(item.key);
    try {
      await onCreateFromCatalog(item, audience);
    } finally {
      setBusy(null);
    }
  }

  async function submitCustom() {
    if (!cKey.trim() || !cLabel.trim()) {
      void openAlert({
        title: "Faltando",
        message: "Key e Rótulo são obrigatórios."
      });
      return;
    }
    setBusy("custom");
    try {
      await onCreateCustom({
        key: cKey,
        label: cLabel,
        description: cDesc,
        platform: cPlatform,
        audience: cAudience
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border bg-background shadow-xl">
        <header class="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h2 class="text-lg font-semibold">Nova feature flag</h2>
            <p class="mt-0.5 text-xs text-muted-foreground">
              Escolha uma das features <strong>já cadastradas no código</strong>{" "}
              (você não precisa decorar key) ou crie uma personalizada.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            class="rounded-md p-1.5 hover:bg-muted"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex gap-1 border-b bg-muted/30 px-5 py-2">
          <button
            type="button"
            onClick={() => setTab("catalog")}
            class={`rounded-md px-3 py-1.5 text-sm font-medium ${
              tab === "catalog"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Sparkles className="mr-1.5 inline size-3.5" />
            Conhecidas ({availableFromCatalog.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("custom")}
            class={`rounded-md px-3 py-1.5 text-sm font-medium ${
              tab === "custom"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Wand2 className="mr-1.5 inline size-3.5" />
            Personalizada
          </button>
        </div>

        <div class="flex-1 overflow-y-auto px-5 py-4">
          {tab === "catalog" ? (
            <CatalogPicker
              available={filteredCatalog}
              alreadyCreated={alreadyCreatedFromCatalog}
              query={query}
              onQuery={setQuery}
              busy={busy}
              onPick={pickFromCatalog}
            />
          ) : (
            <CustomForm
              busy={busy === "custom"}
              cKey={cKey}
              cLabel={cLabel}
              cDesc={cDesc}
              cPlatform={cPlatform}
              cAudience={cAudience}
              setCKey={setCKey}
              setCLabel={setCLabel}
              setCDesc={setCDesc}
              setCPlatform={setCPlatform}
              setCAudience={setCAudience}
              onSubmit={submitCustom}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function CatalogPicker({
  available,
  alreadyCreated,
  query,
  onQuery,
  busy,
  onPick
}: {
  available: KnownFlag[];
  alreadyCreated: KnownFlag[];
  query: string;
  onQuery: (q: string) => void;
  busy: string | null;
  onPick: (item: KnownFlag, audience: FeatureFlagAudience) => Promise<void>;
}) {
  return (
    <div class="space-y-3">
      <label class="block">
        <span class="sr-only">Filtrar catálogo</span>
        <div class="relative">
          <Search class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onInput={(e) => onQuery((e.target as HTMLInputElement).value)}
            placeholder="Buscar pelo nome ou key…"
            class="w-full rounded-md border bg-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
      </label>

      {available.length === 0 ? (
        <div class="rounded-md border border-dashed border-emerald-500/40 bg-emerald-500/5 px-4 py-6 text-center text-sm">
          <CheckCircle2 class="mx-auto mb-2 size-6 text-emerald-600" />
          <strong>Todas as features conhecidas já estão cadastradas.</strong>
          <p class="mt-1 text-xs text-muted-foreground">
            Se você está adicionando uma flag nova no código, crie pela aba{" "}
            <strong>Personalizada</strong> e depois adicione a entrada em{" "}
            <code>apps/manager-new/src/featureFlagsCatalog.ts</code>.
          </p>
        </div>
      ) : (
        <ul class="space-y-2">
          {available.map((f) => (
            <CatalogRow key={f.key} item={f} busy={busy === f.key} onPick={onPick} />
          ))}
        </ul>
      )}

      {alreadyCreated.length > 0 && (
        <details class="rounded-md border bg-muted/30 p-3 text-xs">
          <summary class="cursor-pointer font-medium">
            Já cadastradas ({alreadyCreated.length})
          </summary>
          <ul class="mt-2 space-y-1">
            {alreadyCreated.map((f) => (
              <li key={f.key} class="flex items-center gap-2 text-muted-foreground">
                <CheckCircle2 class="size-3 text-emerald-600" />
                <span class="font-medium">{f.label}</span>
                <code class="text-[10px]">{f.key}</code>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function CatalogRow({
  item,
  busy,
  onPick
}: {
  item: KnownFlag;
  busy: boolean;
  onPick: (item: KnownFlag, audience: FeatureFlagAudience) => Promise<void>;
}) {
  return (
    <li class="rounded-lg border bg-card p-4">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <h3 class="text-sm font-semibold">{item.label}</h3>
            <span class="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              {item.platform}
            </span>
          </div>
          <p class="mt-1 text-xs text-muted-foreground">{item.description}</p>
          <code class="mt-2 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
            {item.key}
          </code>
        </div>
        <div class="flex shrink-0 flex-col gap-1">
          <button
            type="button"
            onClick={() => void onPick(item, "all")}
            disabled={busy}
            class="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            title="Liga pra todo mundo"
          >
            {busy ? (
              <Loader2 class="size-3 animate-spin" />
            ) : (
              <Power class="size-3" />
            )}
            Ligar pra todos
          </button>
          <button
            type="button"
            onClick={() => void onPick(item, "allowlist")}
            disabled={busy}
            class="inline-flex items-center gap-1 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
            title="Cria em modo allowlist (só liberados)"
          >
            <Users class="size-3" />
            Só allowlist
          </button>
        </div>
      </div>
    </li>
  );
}

function CustomForm({
  busy,
  cKey,
  cLabel,
  cDesc,
  cPlatform,
  cAudience,
  setCKey,
  setCLabel,
  setCDesc,
  setCPlatform,
  setCAudience,
  onSubmit
}: {
  busy: boolean;
  cKey: string;
  cLabel: string;
  cDesc: string;
  cPlatform: FeatureFlagPlatform;
  cAudience: FeatureFlagAudience;
  setCKey: (v: string) => void;
  setCLabel: (v: string) => void;
  setCDesc: (v: string) => void;
  setCPlatform: (v: FeatureFlagPlatform) => void;
  setCAudience: (v: FeatureFlagAudience) => void;
  onSubmit: () => Promise<void>;
}) {
  return (
    <div class="space-y-4">
      <div class="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
        <AlertCircle class="mr-1 inline size-3.5 text-amber-600" />
        Use isso só quando estiver adicionando uma flag <strong>nova</strong>{" "}
        no código que ainda não está no catálogo. Lembre de cadastrar a entrada
        em <code>apps/manager-new/src/featureFlagsCatalog.ts</code> depois pra
        que apareça na aba "Conhecidas" da próxima vez.
      </div>

      <div class="grid grid-cols-2 gap-3">
        <label class="block space-y-1">
          <span class="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Key técnica (snake.dot) *
          </span>
          <input
            type="text"
            value={cKey}
            onInput={(e) => setCKey((e.target as HTMLInputElement).value)}
            placeholder="student.menu.aistudy"
            class="w-full rounded-md border bg-background px-3 py-1.5 font-mono text-xs"
          />
        </label>
        <label class="block space-y-1">
          <span class="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Rótulo amigável *
          </span>
          <input
            type="text"
            value={cLabel}
            onInput={(e) => setCLabel((e.target as HTMLInputElement).value)}
            placeholder="Estudar com IA (aluno)"
            class="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
          />
        </label>
      </div>

      <label class="block space-y-1">
        <span class="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Descrição
        </span>
        <textarea
          rows={3}
          value={cDesc}
          onInput={(e) => setCDesc((e.target as HTMLTextAreaElement).value)}
          placeholder="O que essa flag liga ou desliga? Onde aparece? Qual o efeito visível?"
          class="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
        />
      </label>

      <div class="grid grid-cols-2 gap-3">
        <label class="block space-y-1">
          <span class="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Plataforma alvo
          </span>
          <select
            value={cPlatform}
            onChange={(e) =>
              setCPlatform((e.target as HTMLSelectElement).value as FeatureFlagPlatform)
            }
            class="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
          >
            <option value="any">Qualquer</option>
            <option value="platform">Admin Plataforma</option>
            <option value="company">Empresa</option>
            <option value="teacher">Professor</option>
            <option value="student">Aluno</option>
          </select>
        </label>
        <label class="block space-y-1">
          <span class="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Audiência inicial
          </span>
          <select
            value={cAudience}
            onChange={(e) =>
              setCAudience((e.target as HTMLSelectElement).value as FeatureFlagAudience)
            }
            class="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
          >
            <option value="allowlist">Allowlist (só liberados)</option>
            <option value="all">Todos</option>
            <option value="off">Desligada</option>
          </select>
        </label>
      </div>

      <div class="flex justify-end">
        <button
          type="button"
          onClick={() => void onSubmit()}
          disabled={busy || !cKey.trim() || !cLabel.trim()}
          class="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {busy ? <Loader2 class="size-3.5 animate-spin" /> : <Plus class="size-3.5" />}
          Criar flag
        </button>
      </div>
    </div>
  );
}
